import React, { useState } from 'react';
import axios from 'axios';
import {
    Button,
    Box,
    Modal,
    Typography,
    Switch,
    IconButton,
    Slider,
    Chip,
    Stack,
    Paper,
    Divider,
    CircularProgress,
    TextField
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsIcon from '@mui/icons-material/Settings';
import ModalConfiguracionGeocerca from "./ModalConfiguracionGeocerca";

const API_URL = "https://cronos.datachile.site/token/api/web/";

const ModalConfiguracion = ({ vehiculoSeleccionado }) => {
    const [open, setOpen] = useState(false);
    const [alertas, setAlertas] = useState([]);
    const [relayLoading, setRelayLoading] = useState(false);
    const [relayEstado, setRelayEstado] = useState(null);
    const [openRelayModal, setOpenRelayModal] = useState(false);
    const [relayAccion, setRelayAccion] = useState(null);
    const [relayPassword, setRelayPassword] = useState('');
    const token = localStorage.getItem("token");

    // =====================================================
    // CARGAR ALERTAS
    // =====================================================
    const cargarAlertas = async () => {
        if (!vehiculoSeleccionado) return;
        try {
            const res = await axios.post(
                `${API_URL}Alertas/ObtenerAlertas.php`,
                {
                    vehiculo_id: vehiculoSeleccionado.idVehiculo
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            const data = Array.isArray(res.data) ? res.data : [];
            const normalizadas = data.map(a => ({
                ...a,
                geocercas: Array.isArray(a.geocercas) ? a.geocercas : []
            }));
            setAlertas(normalizadas);
        } catch (error) {
            console.error("Error cargando alertas:", error);
            setAlertas([]);
        }
    };

    // =====================================================
    // OPEN / CLOSE
    // =====================================================
    const handleOpen = () => {
        setOpen(true);
        cargarAlertas();
    };

    const handleClose = () => {
        setOpen(false);
    };

    // =====================================================
    // TOGGLE ALERTA & UMBRAL
    // =====================================================
    const handleToggle = (idRegla) => {
        setAlertas(prev => prev.map(a => 
            a.idRegla === idRegla ? { ...a, activo_regla: !a.activo_regla } : a
        ));
    };

    const handleUmbralChange = (idRegla, value) => {
        setAlertas(prev => prev.map(a => 
            a.idRegla === idRegla ? { ...a, umbral: value } : a
        ));
    };

    // =====================================================
    // RELAY LOGIC
    // =====================================================
    const abrirModalRelay = (accion) => {
        setRelayAccion(accion);
        setRelayPassword('');
        setRelayEstado(null);
        setOpenRelayModal(true);
    };

    const handleRelay = async () => {
        if (!vehiculoSeleccionado) return;
        if (!relayPassword || relayPassword.length !== 6) {
            setRelayEstado({ ok: false, mensaje: 'La contraseña debe tener 6 dígitos' });
            return;
        }
        try {
            setRelayLoading(true);
            setRelayEstado(null);
            const response = await axios.post(
                `${API_URL}Vehiculos/IngresarRelay.php`,
                {
                    idVehiculo: vehiculoSeleccionado.idVehiculo,
                    accion: relayAccion,
                    password: relayPassword
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            console.log('RELAY RESPONSE:', response.data);
            setRelayEstado({
                ok: true,
                mensaje: relayAccion === 'off' ? 'Comando de apagado enviado' : 'Comando de encendido enviado'
            });
            setOpenRelayModal(false);
        } catch (err) {
            console.error('ERROR RELAY:', err);
            setRelayEstado({
                ok: false,
                mensaje: err?.response?.data?.message || 'Error enviando comando'
            });
        } finally {
            setRelayLoading(false);
        }
    };

    // =====================================================
    // GUARDAR ALERTAS
    // =====================================================
    const handleGuardar = async () => {
        try {
            const payload = alertas.map(a => ({
                idRegla: a.idRegla,
                activo: a.activo_regla ? 1 : 0,
                umbral: a.umbral ?? null,
                geocercas: a.geocercas || []
            }));
            await axios.post(
                `${API_URL}Alertas/GuardarAlertas.php`,
                {
                    vehiculo_id: vehiculoSeleccionado.idVehiculo,
                    alertas: payload
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            handleClose();
        } catch (err) {
            console.error("Error guardando:", err);
        }
    };

    // =====================================================
    // CONFIG VISUAL
    // =====================================================
    const getConfig = (tipo) => {
        switch (tipo) {
            case "velocidad":
                return { icon: <SpeedIcon />, color: "#FF9800", label: "Exceso de velocidad" };
            case "geocerca":
                return { icon: <LocationOnIcon />, color: "#9C27B0", label: "Geocercas" };
            case "ignicion":
                return { icon: <PowerSettingsNewIcon />, color: "#4CAF50", label: "Encendido / Apagado" };
            default:
                return { icon: <SettingsIcon />, color: "#607D8B", label: tipo };
        }
    };

    return (
        <>
            <IconButton size="small" onClick={handleOpen}>
                <SettingsIcon sx={{ color: "#2b90fd" }} />
            </IconButton>

            {/* MODAL PRINCIPAL */}
            <Modal open={open} onClose={handleClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '95%',
                        maxWidth: 900,
                        bgcolor: '#F9FAFB',
                        borderRadius: 3,
                        boxShadow: 24,
                        overflow: 'hidden'
                    }}
                >
                    {/* HEADER */}
                    <Box sx={{ p: 3, background: "linear-gradient(135deg, #2b90fd, #1976d2)", color: "#fff" }}>
                        <Typography variant="h6" fontWeight={600}>⚙️ Configuración</Typography>
                        <Typography fontSize={13} sx={{ opacity: 0.8 }}>Gestiona alertas y control remoto</Typography>
                    </Box>

                    {/* BODY */}
                    <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
                        
                        {/* RELAY */}
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #E5E7EB' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                                <Box>
                                    <Typography fontWeight={700} fontSize={18}>🔒 Control de Relay</Typography>
                                    <Typography fontSize={13} color="text.secondary">Encendido y apagado remoto del vehículo</Typography>
                                </Box>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="contained" color="error" disabled={relayLoading} onClick={() => abrirModalRelay('off')}
                                        startIcon={relayLoading ? <CircularProgress size={18} color="inherit" /> : <PowerSettingsNewIcon />}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Apagar
                                    </Button>
                                    <Button
                                        variant="contained" color="success" disabled={relayLoading} onClick={() => abrirModalRelay('on')}
                                        startIcon={relayLoading ? <CircularProgress size={18} color="inherit" /> : <PowerSettingsNewIcon />}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Encender
                                    </Button>
                                </Stack>
                            </Stack>
                            {relayEstado && (
                                <Box mt={2}>
                                    <Chip
                                        label={relayEstado.mensaje}
                                        sx={{
                                            background: relayEstado.ok ? '#E8F5E9' : '#FFEBEE',
                                            color: relayEstado.ok ? '#2E7D32' : '#C62828',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                            )}
                        </Paper>
                        
                        <Divider sx={{ mb: 3 }} />

                        {/* ALERTAS */}
                        <Stack spacing={2}>
                            {alertas.map((a) => {
                                const cfg = getConfig(a.tipo);
                                return (
                                    <Paper
                                        key={a.idRegla}
                                        sx={{
                                            p: 2, borderRadius: 3, transition: "0.2s",
                                            border: `1px solid ${a.activo_regla ? cfg.color : '#E5E7EB'}`,
                                            background: a.activo_regla ? "#fff" : "#f3f4f6"
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Box sx={{ background: cfg.color, color: "#fff", borderRadius: 2, p: 0.8, display: "flex" }}>
                                                    {cfg.icon}
                                                </Box>
                                                <Box>
                                                    <Typography fontWeight={600}>{cfg.label}</Typography>
                                                    <Chip
                                                        size="small"
                                                        label={a.activo_regla ? "Activa" : "No activada"}
                                                        sx={{ mt: 0.5, color: "#fff", background: a.activo_regla ? "#4CAF50" : "#F44336" }}
                                                    />
                                                </Box>
                                            </Stack>
                                            <Switch checked={a.activo_regla} onChange={() => handleToggle(a.idRegla)} />
                                        </Stack>

                                        {/* VELOCIDAD */}
                                        {a.tipo === "velocidad" && a.activo_regla && (
                                            <Box mt={2}>
                                                <Typography fontSize={12} color="text.secondary">
                                                    Límite: <b> {a.umbral || 0} km/h</b>
                                                </Typography>
                                                <Slider
                                                    value={a.umbral || 0} min={20} max={180}
                                                    onChange={(e, val) => handleUmbralChange(a.idRegla, val)}
                                                />
                                            </Box>
                                        )}

                                        {/* GEOCERCAS */}
                                        {a.tipo === "geocerca" && (
                                            <Box mt={2}>
                                                <Typography fontSize={12} color="text.secondary" mb={1}>
                                                    {a.geocercas?.length || 0} zona(s) configurada(s)
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {a.geocercas?.length > 0 ? (
                                                        a.geocercas.map((g) => (
                                                            <Chip
                                                                key={g.id} label={g.nombre}
                                                                sx={{ background: "#EDE7F6", color: "#5E35B1", fontWeight: 500 }}
                                                            />
                                                        ))
                                                    ) : (
                                                        <Chip label="Sin geocercas" sx={{ background: "#FFEBEE", color: "#C62828" }} />
                                                    )}
                                                </Stack>
                                                <Box mt={1}>
                                                    <ModalConfiguracionGeocerca
                                                        geocercas={a.geocercas}
                                                        onSave={(nuevas) => {
                                                            setAlertas(prev => prev.map(x => 
                                                                x.idRegla === a.idRegla ? { ...x, geocercas: nuevas } : x
                                                            ));
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Stack>

                        {/* SAVE */}
                        <Box mt={3} textAlign="right">
                            <Button variant="contained" onClick={handleGuardar} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                                Guardar cambios
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* MODAL PASSWORD RELAY */}
            <Modal open={openRelayModal} onClose={() => setOpenRelayModal(false)}>
                <Box
                    sx={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        width: 380, bgcolor: '#fff', borderRadius: 3, boxShadow: 24, p: 3
                    }}
                >
                    <Typography variant="h6" fontWeight={700} mb={1}>🔐 Confirmar comando</Typography>
                    <Typography fontSize={13} color="text.secondary" mb={3}>Ingresa la contraseña del GPS</Typography>
                    <TextField
                        fullWidth type="password" label="Contraseña GPS" value={relayPassword}
                        inputProps={{ maxLength: 6 }}
                        onChange={(e) => setRelayPassword(e.target.value.replace(/\D/g, ''))}
                    />
                    <Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => setOpenRelayModal(false)}>Cancelar</Button>
                        <Button
                            variant="contained" disabled={relayLoading} onClick={handleRelay}
                            color={relayAccion === 'off' ? 'error' : 'success'}
                        >
                            {relayLoading ? 'Enviando...' : relayAccion === 'off' ? 'Apagar vehículo' : 'Encender vehículo'}
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export default ModalConfiguracion;